/**
 * Utility functions for Phone formatting, masking, and sanitization.
 * Mask format: +99 (99) 999-999-999
 * Raw / DB format: 9999999999999 (digits only)
 */

/**
 * Strips all non-digit characters from the input string.
 */
export function unmaskPhone(value?: string | null): string {
  if (!value) return '';
  return value.replace(/\D/g, '');
}

/**
 * Formats a phone string into the mask `+99 (99) 999-999-999`.
 * Accepts raw digits (e.g. "5511988887777", "11988887777", etc.) or already formatted strings.
 */
export function formatPhone(value?: string | null): string {
  if (!value) return '';
  
  let digits = value.replace(/\D/g, '');
  if (!digits) return '';

  // If phone has 10 or 11 digits without country code, prepend 55 (Brazil)
  if ((digits.length === 10 || digits.length === 11) && !digits.startsWith('55')) {
    digits = `55${digits}`;
  }

  // Limit to maximum 13 digits
  digits = digits.slice(0, 13);

  const d0_2 = digits.slice(0, 2);
  const d2_4 = digits.slice(2, 4);
  const d4_7 = digits.slice(4, 7);
  const d7_10 = digits.slice(7, 10);
  const d10_13 = digits.slice(10, 13);

  if (digits.length <= 2) {
    return `+${d0_2}`;
  }
  if (digits.length <= 4) {
    return `+${d0_2} (${d2_4}`;
  }
  if (digits.length <= 7) {
    return `+${d0_2} (${d2_4}) ${d4_7}`;
  }
  if (digits.length <= 10) {
    return `+${d0_2} (${d2_4}) ${d4_7}-${d7_10}`;
  }
  return `+${d0_2} (${d2_4}) ${d4_7}-${d7_10}-${d10_13}`;
}

/**
 * Progressively masks phone input as the user types (only allows digits).
 */
export function maskPhoneInput(inputValue: string): string {
  if (!inputValue) return '';
  const rawDigits = inputValue.replace(/\D/g, '').slice(0, 13);
  if (!rawDigits) return '';

  const d0_2 = rawDigits.slice(0, 2);
  const d2_4 = rawDigits.slice(2, 4);
  const d4_7 = rawDigits.slice(4, 7);
  const d7_10 = rawDigits.slice(7, 10);
  const d10_13 = rawDigits.slice(10, 13);

  if (rawDigits.length <= 2) {
    return `+${d0_2}`;
  }
  if (rawDigits.length <= 4) {
    return `+${d0_2} (${d2_4}`;
  }
  if (rawDigits.length <= 7) {
    return `+${d0_2} (${d2_4}) ${d4_7}`;
  }
  if (rawDigits.length <= 10) {
    return `+${d0_2} (${d2_4}) ${d4_7}-${d7_10}`;
  }
  return `+${d0_2} (${d2_4}) ${d4_7}-${d7_10}-${d10_13}`;
}

/**
 * Validates whether a phone number has a valid digit count.
 */
export function validatePhoneNumber(phoneRaw?: string | null): boolean {
  if (!phoneRaw) return false;
  const digits = phoneRaw.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 13;
}
